import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaFilter,
  FaPlus,
  FaTools,
  FaTruckMoving
} from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './InvoiceDraftsPage.scss';

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('tr-TR');
};

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const pickText = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim()) || '';

const VAT_OPTIONS = [20, 10, 1, 0];
const DRAFTS_PAGE_SIZE = 5;
const INVOICED_DRAFT_STORAGE_KEY = 'accountingInvoicedDrafts';
const INVOICE_DRAFTS_CACHE_KEY = 'invoiceDraftsCacheV1';
const INVOICE_SENDING_DISABLED = true;

const calculateRentalFeeTotal = (rental) => {
  const rent = Number(rental?.rent || 0);
  const rentalPeriod = Number(rental?.rentalPeriod || 0);
  const extraPeriod = Number(rental?.extraPeriod || 0);
  const extraPeriodMonth = Number(rental?.extraPeriodMonth || 0);
  const extraPeriodRent = Number(rental?.extraPeriodRent || rent);

  const rentWithVat = Math.round(rent * 1.2 * 100) / 100;
  const extraPeriodRentWithVat = Math.round(extraPeriodRent * 1.2 * 100) / 100;

  return (
    Math.round(rentWithVat * rentalPeriod * 100) / 100 +
    Math.round(extraPeriodRentWithVat * extraPeriodMonth * 100) / 100 +
    Math.round((extraPeriodRentWithVat * extraPeriod) / 30 * 100) / 100
  );
};

const calculateOwnerServiceFee = (rental) => {
  const totalFee = calculateRentalFeeTotal(rental);
  const commissionRate = Number(rental?.commissionRate || 0);
  const progressPayment = Math.round(totalFee * (commissionRate / 100) * 100) / 100;
  return Math.round((totalFee - progressPayment) * 100) / 100;
};

const readInvoicedDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(INVOICED_DRAFT_STORAGE_KEY) || '{}');
  } catch (error) {
    console.error('Kesilen fatura kayitlari okunamadi:', error);
    return {};
  }
};

const writeInvoicedDrafts = (value) => {
  try {
    localStorage.setItem(INVOICED_DRAFT_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Kesilen fatura kayitlari yazilamadi:', error);
  }
};

const readInvoiceDraftsCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(INVOICE_DRAFTS_CACHE_KEY) || '{}');
  } catch (error) {
    console.error('Fatura İşlemleri cache okunamadi:', error);
    return {};
  }
};

const writeInvoiceDraftsCache = (value) => {
  try {
    sessionStorage.setItem(INVOICE_DRAFTS_CACHE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Fatura İşlemleri cache yazilamadi:', error);
  }
};

const extractRentalIdsFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental') {
    return [];
  }

  if (Array.isArray(record.rentalIds) && record.rentalIds.length > 0) {
    return record.rentalIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }

  const draftId = String(record.draftId || '');
  const matched = draftId.match(/^rental-[0-9a-f-]{36}-(.+)$/i);
  if (!matched?.[1]) {
    return [];
  }

  return matched[1]
    .split('-')
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
};

const extractRentalCompanyIdFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental') {
    return '';
  }

  const draftId = String(record.draftId || '');
  const sourceId = String(record.sourceId || '');
  const companyIdMatch = draftId.match(/^rental-([0-9a-f-]{36})(?:-|$)/i);

  if (companyIdMatch?.[1]) {
    return companyIdMatch[1];
  }

  const sourceCompanyIdMatch = sourceId.match(/^([0-9a-f-]{36})(?:-|$)/i);
  return sourceCompanyIdMatch?.[1] || '';
};

const extractRentalInstallmentsFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental' || !Array.isArray(record.rentalInstallments)) {
    return [];
  }

  return record.rentalInstallments
    .map((item) => ({
      rentalId: Number(item?.rentalId),
      installmentNo: Number(item?.installmentNo)
    }))
    .filter(
      (item) =>
        !Number.isNaN(item.rentalId) &&
        item.rentalId > 0 &&
        !Number.isNaN(item.installmentNo) &&
        item.installmentNo > 0
    );
};

const extractRentalOneTimeItemIdsFromInvoiceRecord = (record) => {
  if (!record || record.type !== 'rental' || !Array.isArray(record.rentalOneTimeItemIds)) {
    return [];
  }

  return record.rentalOneTimeItemIds
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
};

const getRentalInstallmentCount = (rental) =>
  Math.max(Number(rental?.invoiceInstallmentCount || rental?.rentalPeriod || 0), 0);

const getRentalInvoiceStartDate = (rental) => {
  const invoiceStartDate = rental?.firstInvoiceDate ? new Date(rental.firstInvoiceDate) : null;
  if (invoiceStartDate && !Number.isNaN(invoiceStartDate.getTime())) {
    return invoiceStartDate;
  }

  const rentalStartDate = rental?.rentalStartDate ? new Date(rental.rentalStartDate) : null;
  return rentalStartDate && !Number.isNaN(rentalStartDate.getTime()) ? rentalStartDate : null;
};

const getRentalMonthlyAmount = (rental) => {
  return Number(rental?.rent || 0);
};

const startOfLocalDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addMonthsClamped = (value, monthOffset) => {
  const date = new Date(value);
  const day = date.getDate();
  const target = new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
  const lastDayOfTargetMonth = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();

  target.setDate(Math.min(day, lastDayOfTargetMonth));
  return target;
};

const getDueRentalInstallmentCount = (rental) => {
  const totalInstallments = getRentalInstallmentCount(rental);
  if (totalInstallments === 0) return 0;

  const rentalInvoiceStartDate = getRentalInvoiceStartDate(rental);
  if (!rentalInvoiceStartDate) {
    return totalInstallments;
  }

  const today = startOfLocalDay(new Date());
  const firstDueDate = startOfLocalDay(rentalInvoiceStartDate);

  if (today < firstDueDate) {
    return 0;
  }

  let dueCount = 0;
  for (let index = 0; index < totalInstallments; index += 1) {
    const dueDate = startOfLocalDay(addMonthsClamped(firstDueDate, index));
    if (dueDate <= today) {
      dueCount += 1;
    }
  }

  return dueCount;
};

const InvoiceDraftsPage = () => {
  const initialCache = readInvoiceDraftsCache();
  const [userRole, setUserRole] = useState('');
  const [companies, setCompanies] = useState(() => initialCache.companies || []);
  const [rentalReports, setRentalReports] = useState([]);
  const [rawApprovedRentals, setRawApprovedRentals] = useState(
    () => initialCache.rawApprovedRentals || []
  );
  const [technicalServiceRequests, setTechnicalServiceRequests] = useState(
    () => initialCache.technicalServiceRequests || []
  );
  const [loading, setLoading] = useState(
    () =>
      !(
        (initialCache.companies && initialCache.companies.length) ||
        (initialCache.rawApprovedRentals && initialCache.rawApprovedRentals.length) ||
        (initialCache.technicalServiceRequests && initialCache.technicalServiceRequests.length)
      )
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [draftPage, setDraftPage] = useState(1);
  const [selectedDraftId, setSelectedDraftId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [successNotice, setSuccessNotice] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [invoicedDrafts, setInvoicedDrafts] = useState(() => readInvoicedDrafts());
  const [draftItems, setDraftItems] = useState([]);
  const [draftForm, setDraftForm] = useState({
    companyName: '',
    taxNo: '',
    address: '',
    email: '',
    phone: '',
    note: ''
  });

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setUserRole(
          decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || ''
        );
      }
    } catch (error) {
      console.error('Fatura İşlemleri rol bilgisi okunamadı:', error);
    }
  }, []);

  useEffect(() => {
    let initialLoadFinished = false;
    const finishInitialLoad = () => {
      if (!initialLoadFinished) {
        initialLoadFinished = true;
        setLoading(false);
      }
    };

    setLoading(true);
    setErrorMessage('');
    setRentalReports([]);

    api
      .get('/Company')
      .then((companiesResponse) => {
        setCompanies(companiesResponse?.data?.data || []);
      })
      .catch((error) => {
        console.error('Firma listesi yüklenemedi:', error);
        setErrorMessage('Firma verileri alınamadı.');
      })
      .finally(finishInitialLoad);

    api
      .get('/Rental', { params: { serviceStatus: 'Approved' } })
      .then((rentalsResponse) => {
        const fetchedApprovedRentals = rentalsResponse?.data?.data || [];
        setRawApprovedRentals(fetchedApprovedRentals);
      })
      .catch((rentalError) => {
        console.error('Kiralama faturaları yüklenemedi:', rentalError);
      })
      .finally(finishInitialLoad);

    api
      .get('/TechnicalService/requests')
      .then((technicalResponse) => {
        setTechnicalServiceRequests(technicalResponse?.data?.data || []);
      })
      .catch((technicalError) => {
        console.error('Teknik servis taslakları yüklenemedi:', technicalError);
      })
      .finally(finishInitialLoad);
  }, []);

  useEffect(() => {
    writeInvoiceDraftsCache({
      companies,
      rawApprovedRentals,
      technicalServiceRequests
    });
  }, [companies, rawApprovedRentals, technicalServiceRequests]);

  const companyMap = useMemo(
    () =>
      companies.reduce((acc, company) => {
        acc[company.id] = company;
        return acc;
      }, {}),
    [companies]
  );

  const draftCandidates = useMemo(() => {
    const fullyInvoicedRentalIds = new Set(
      Object.values(invoicedDrafts)
        .filter(
          (record) =>
            record?.type === 'rental' &&
            !Array.isArray(record?.rentalInstallments) &&
            Array.isArray(record?.rentalIds) &&
            record.rentalIds.length > 0
        )
        .flatMap((record) => extractRentalIdsFromInvoiceRecord(record))
    );
    const invoicedRentalInstallmentsMap = Object.values(invoicedDrafts).reduce((acc, record) => {
      extractRentalInstallmentsFromInvoiceRecord(record).forEach((item) => {
        if (!acc[item.rentalId]) {
          acc[item.rentalId] = new Set();
        }
        acc[item.rentalId].add(item.installmentNo);
      });
      return acc;
    }, {});
    const invoicedRentalOneTimeItemIdsMap = Object.values(invoicedDrafts).reduce((acc, record) => {
      extractRentalOneTimeItemIdsFromInvoiceRecord(record).forEach((itemId) => {
        acc.add(itemId);
      });
      return acc;
    }, new Set());

    const legacyRentalInvoiceCutoffByCompany = Object.values(invoicedDrafts).reduce(
      (acc, record) => {
        if (record?.type !== 'rental' || (record?.rentalIds || []).length) {
          return acc;
        }

        const companyId = extractRentalCompanyIdFromInvoiceRecord(record);
        const sentAt = record?.sentAt ? new Date(record.sentAt) : null;

        if (!companyId || !sentAt || Number.isNaN(sentAt.getTime())) {
          return acc;
        }

        const currentCutoff = acc[companyId];
        if (!currentCutoff || sentAt > currentCutoff) {
          acc[companyId] = sentAt;
        }

        return acc;
      },
      {}
    );

    const rentalDrafts = [];

    const rentalDraftIds = new Set(rentalDrafts.map((draft) => String(draft.sourceId || draft.id)));

    const fallbackRentalDrafts = Object.values(
      rawApprovedRentals.reduce((acc, rental) => {
        const rentalId = Number(rental?.id);
        if (fullyInvoicedRentalIds.has(rentalId)) {
          return acc;
        }

        const ownerId = rental?.rentingCompanyId;
        const legacyCutoff = legacyRentalInvoiceCutoffByCompany[ownerId];
        if (legacyCutoff) {
          const parsedRentalStart = rental?.rentalStartDate ? new Date(rental.rentalStartDate) : null;
          if (parsedRentalStart && !Number.isNaN(parsedRentalStart.getTime()) && parsedRentalStart <= legacyCutoff) {
            return acc;
          }
        }

        if (!ownerId || rentalDraftIds.has(String(rentalId))) {
          return acc;
        }

        const dueInstallmentCount = getDueRentalInstallmentCount(rental);
        const billedInstallments = invoicedRentalInstallmentsMap[rentalId] || new Set();
        const pendingInstallments = Array.from({ length: dueInstallmentCount }, (_, index) => index + 1).filter(
          (installmentNo) => !billedInstallments.has(installmentNo)
        );
        const nextPendingInstallment = pendingInstallments[0] || null;
        const visibleInstallments = nextPendingInstallment ? [nextPendingInstallment] : [];
        const monthlyAmount = getRentalMonthlyAmount(rental);
        const rentalLineItems = (rental?.lineItems || rental?.LineItems || [])
          .map((item, index) => ({
            id: Number(item?.id || 0),
            description: pickText(item?.description, `Ek Kalem ${index + 1}`),
            quantity: Number(item?.quantity || 1),
            unitPrice: Number(item?.unitPrice || item?.amount || 0),
            amount: Number(item?.amount || 0)
          }))
          .filter(
            (item) =>
              item.description &&
              item.quantity > 0 &&
              item.unitPrice > 0 &&
              (item.amount > 0 || item.quantity * item.unitPrice > 0)
          );
        const shouldIncludeOneTimeItems = nextPendingInstallment === 1 || (!nextPendingInstallment && billedInstallments.has(1));
        const pendingOneTimeItems = shouldIncludeOneTimeItems
          ? rentalLineItems.filter(
              (item) => item.id > 0 && !invoicedRentalOneTimeItemIdsMap.has(item.id)
            )
          : [];

        if ((monthlyAmount <= 0 && pendingOneTimeItems.length === 0) || (visibleInstallments.length === 0 && pendingOneTimeItems.length === 0)) {
          return acc;
        }

        const company = companyMap[ownerId] || {};
        const currentMachineLabel = rental.towerCrane || `Vinç #${rental.towerCraneId}`;
        const installmentItems = visibleInstallments.map((installmentNo) => {
          const installmentDate = getRentalInvoiceStartDate(rental);
          if (installmentDate && !Number.isNaN(installmentDate.getTime())) {
            installmentDate.setMonth(installmentDate.getMonth() + (installmentNo - 1));
          }

          return {
            id: `${ownerId}-rental-${rentalId}-${installmentNo}`,
            description: `${currentMachineLabel} ${installmentNo}. Ay Kira Bedeli`,
            quantity: 1,
            unitPrice: monthlyAmount,
            total: monthlyAmount,
            installmentNo,
            rentalId,
            installmentDate: installmentDate && !Number.isNaN(installmentDate.getTime())
              ? installmentDate.toISOString()
              : null
          };
        });
        const oneTimeItems = pendingOneTimeItems.map((item) => ({
          id: `${ownerId}-rental-${rentalId}-onetime-${item.id}`,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.quantity * item.unitPrice,
          rentalId,
          oneTimeItemId: item.id,
          installmentNo: 1
        }));
        const detailItems = [...installmentItems, ...oneTimeItems];
        const invoiceLineTotal = detailItems.reduce((sum, item) => sum + Number(item.total || 0), 0);
        const draftItems = invoiceLineTotal > 0
          ? [
              {
                id: `${ownerId}-rental-${rentalId}-aggregate`,
                description: 'Kule Vinç Kiralama Operatörsüz',
                quantity: 1,
                unitPrice: invoiceLineTotal,
                total: invoiceLineTotal,
                rentalId
              }
            ]
          : [];

        if (!acc[rentalId]) {
          acc[rentalId] = {
            id: `rental-${ownerId}-${rentalId}`,
            type: 'rental',
            typeLabel: 'Kiralama',
            sourceType: 'rental',
            companyId: ownerId,
            companyName: pickText(rental.rentingCompany, company.name, 'Firma belirtilmedi'),
            title: `${currentMachineLabel} kiralama faturası`,
            taxNo: pickText(company.taxNo),
            address: pickText(company.address),
            email: pickText(company.email),
            phone: pickText(company.phoneNumber1, company.phoneNumber2),
            amount: 0,
            itemCount: 0,
            machineLabel: currentMachineLabel,
            dateLabel: '',
            sourceLabel: '',
            items: [],
            note: '',
            rentalIds: [],
            rentalInstallments: [],
            rentalOneTimeItemIds: [],
            sourceId: String(rentalId)
          };
        }

        acc[rentalId].amount += invoiceLineTotal;
        acc[rentalId].rentalIds = [...new Set([...(acc[rentalId].rentalIds || []), rentalId])];
        acc[rentalId].rentalInstallments = [
          ...(acc[rentalId].rentalInstallments || []),
          ...installmentItems.map((item) => ({ rentalId: item.rentalId, installmentNo: item.installmentNo }))
        ];
        acc[rentalId].rentalOneTimeItemIds = [
          ...new Set([
            ...(acc[rentalId].rentalOneTimeItemIds || []),
            ...oneTimeItems.map((item) => item.oneTimeItemId).filter(Boolean)
          ])
        ];
        acc[rentalId].items.push(...draftItems);
        const monthlyLineCount = installmentItems.length;
        const oneTimeLineCount = oneTimeItems.length;
        if (monthlyLineCount > 0 && oneTimeLineCount > 0) {
          acc[rentalId].sourceLabel = `${installmentItems[0]?.installmentNo || 1}. ay kira + ${oneTimeLineCount} ilk fatura kalemi`;
        } else if (monthlyLineCount > 0) {
          acc[rentalId].sourceLabel = `${installmentItems[0]?.installmentNo || 1}. ay kira faturası`;
        } else {
          acc[rentalId].sourceLabel = `${oneTimeLineCount} ilk fatura kalemi`;
        }
        acc[rentalId].dateLabel = `${formatDate(rental.rentalStartDate)} - ${formatDate(rental.rentalEndDate)}`;
        acc[rentalId].itemCount = acc[rentalId].items.length;
        const installmentKey = (acc[rentalId].rentalInstallments || [])
          .slice()
          .sort((a, b) =>
            Number(a.rentalId) === Number(b.rentalId)
              ? Number(a.installmentNo) - Number(b.installmentNo)
              : Number(a.rentalId) - Number(b.rentalId)
          )
          .map((item) => `${item.rentalId}:${item.installmentNo}`)
          .join('-');
        const oneTimeKey = (acc[rentalId].rentalOneTimeItemIds || [])
          .slice()
          .sort((a, b) => Number(a) - Number(b))
          .join('-');
        acc[rentalId].id = `rental-${ownerId}-${[installmentKey, oneTimeKey].filter(Boolean).join('--')}`;

        return acc;
      }, {})
    ).map((draft) => ({
      ...draft,
      title: `${draft.machineLabel} kiralama faturası`
    }));

    const technicalDrafts = technicalServiceRequests
      .map((request) => {
        const normalizedStatus = normalizeStatus(request.status);
        const amount = Number(request.totalPrice || 0);

        if (normalizedStatus !== 7 || amount <= 0) return null;

        const company = companyMap[request.requestingCompanyId] || {};
        const workLogs = Array.isArray(request.workLogs) ? request.workLogs : [];
        const workLogParts = workLogs.flatMap((workLog) => workLog.usedParts || []);
        const directParts = Array.isArray(request.usedParts) ? request.usedParts : [];
        const parts = [...directParts, ...workLogParts];

        const partItems = parts
          .filter((part) => pickText(part.itemName, part.partName))
          .map((part, index) => {
            const quantity = Number(part.quantity || 1);
            const unitPrice = Number(part.unitPrice || 0);
            const unitLabel = pickText(part.unit);
            const noteLabel = pickText(part.note);
            const itemLabel = pickText(part.itemName, part.partName, `Kalem ${index + 1}`);

            return {
              id: `${request.id}-part-${part.id || index}`,
              description: `${itemLabel}${unitLabel ? ` (${unitLabel})` : ''}${
                noteLabel ? ` - ${noteLabel}` : ''
              }`,
              quantity,
              unitPrice,
              total: Number(part.totalPrice || 0) || quantity * unitPrice
            };
          });

        const workLogItems = workLogs
          .filter(
            (workLog) =>
              (!Array.isArray(workLog.usedParts) || workLog.usedParts.length === 0) &&
              pickText(workLog.description)
          )
          .map((workLog, index) => ({
            id: `${request.id}-worklog-${workLog.id || index}`,
            description: workLog.description,
            quantity: 1,
            unitPrice: Number(workLog.unitPrice || 0),
            total: Number(workLog.totalPrice || 0) || Number(workLog.unitPrice || 0)
          }));

        const items = [...partItems, ...workLogItems].length
          ? [...partItems, ...workLogItems]
          : [
              {
                id: `${request.id}-service-total`,
                description: 'Teknik servis toplamı',
                quantity: 1,
                unitPrice: amount,
                total: amount
              }
            ];

        const isExternal = Boolean(request.externalMachineName || request.externalMachineModel);

        return {
          id: `technical-${request.id}`,
          type: isExternal ? 'external-service' : 'internal-service',
          typeLabel: isExternal ? 'Dış Teknik Servis' : 'İç Teknik Servis',
          sourceType: 'technical-service',
          sourceId: String(request.id),
          companyId: request.requestingCompanyId,
          companyName: pickText(request.requestingCompanyName, company.name, 'Firma belirtilmedi'),
          title:
            request.title ||
            request.subject ||
            request.requestNo ||
            `Teknik servis #${request.id}`,
          taxNo: pickText(company.taxNo),
          address: pickText(company.address),
          email: pickText(company.email),
          phone: pickText(company.phoneNumber1, company.phoneNumber2),
          amount,
          itemCount: items.length,
          machineLabel: pickText(
            request.towerCraneSerialNo,
            request.externalMachineName,
            request.externalMachineModel,
            'Makine belirtilmedi'
          ),
          dateLabel: formatDate(
            request.completedDate || request.lastModifiedDate || request.requestedDate
          ),
          sourceLabel: request.requestNo || `Talep #${request.id}`,
          items,
          note: '',
          isAlreadyInvoiced: Boolean(request.isInvoiceCut),
          invoiceRecord: request.isInvoiceCut
            ? {
                draftId: `technical-${request.id}`,
                type: isExternal ? 'external-service' : 'internal-service',
                sourceId: String(request.id),
                companyName: pickText(request.requestingCompanyName, company.name, 'Firma belirtilmedi'),
                invoiceNumber: request.invoiceNumber || '',
                ettn: request.invoiceEttn || '',
                sentAt: request.invoiceCutDate || null,
                statusLabel: 'Tamamlandi',
                infoLabel: 'Fatura Kesildi'
              }
            : null
        };
      })
      .filter(Boolean);

    return [...technicalDrafts, ...rentalDrafts, ...fallbackRentalDrafts].sort(
      (a, b) => Number(b.amount || 0) - Number(a.amount || 0)
    );
  }, [companyMap, invoicedDrafts, rawApprovedRentals, rentalReports, technicalServiceRequests]);

  const filteredDrafts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return draftCandidates.filter((draft) => {
      const pendingMatches = !draft.isAlreadyInvoiced && !invoicedDrafts[draft.id];
      const roleMatches =
        userRole === 'RentalAccountant'
          ? draft.type === 'rental'
          : userRole === 'TechnicalServiceAccountant'
            ? draft.sourceType === 'technical-service'
            : true;
      const typeMatches = typeFilter === 'all' || draft.type === typeFilter;
      const companyMatches = companyFilter === 'all' || draft.companyId === companyFilter;
      const searchMatches =
        !normalizedSearch ||
        [draft.companyName, draft.title, draft.machineLabel, draft.typeLabel, draft.sourceLabel]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);

      return pendingMatches && roleMatches && typeMatches && companyMatches && searchMatches;
    });
  }, [companyFilter, draftCandidates, invoicedDrafts, searchTerm, typeFilter, userRole]);

  const draftPageCount = Math.max(1, Math.ceil(filteredDrafts.length / DRAFTS_PAGE_SIZE));
  const paginatedDrafts = useMemo(() => {
    const startIndex = (draftPage - 1) * DRAFTS_PAGE_SIZE;
    return filteredDrafts.slice(startIndex, startIndex + DRAFTS_PAGE_SIZE);
  }, [draftPage, filteredDrafts]);

  useEffect(() => {
    setDraftPage(1);
  }, [companyFilter, searchTerm, typeFilter]);

  useEffect(() => {
    if (draftPage > draftPageCount) {
      setDraftPage(draftPageCount);
    }
  }, [draftPage, draftPageCount]);

  const selectedDraft = useMemo(() => {
    if (!filteredDrafts.length) return null;
    return filteredDrafts.find((draft) => draft.id === selectedDraftId) || filteredDrafts[0];
  }, [filteredDrafts, selectedDraftId]);

  const isReadOnlyOwner = false;

  useEffect(() => {
    if (isReadOnlyOwner) {
      setSelectedDraftId('');
      setPreviewOpen(false);
      setActionMessage('');
      setActionError('');
      return;
    }

    if (selectedDraft) {
      setSelectedDraftId(selectedDraft.id);
      setDraftItems(
        (selectedDraft.items || []).map((item, index) => ({
          ...item,
          vatRate: Number(item.vatRate ?? 20),
          localId: item.id || `${selectedDraft.id}-item-${index}`
        }))
      );
      setDraftForm({
        companyName: selectedDraft.companyName || '',
        taxNo: selectedDraft.taxNo || '',
        address: selectedDraft.address || '',
        email: selectedDraft.email || '',
        phone: selectedDraft.phone || '',
        note: selectedDraft.note || ''
      });
    } else {
      setSelectedDraftId('');
      setDraftItems([]);
      setDraftForm({
        companyName: '',
        taxNo: '',
        address: '',
        email: '',
        phone: '',
        note: ''
      });
    }
    setActionMessage('');
    setActionError('');
  }, [isReadOnlyOwner, selectedDraft]);

  useEffect(() => {
    if (!selectedDraft || selectedDraft.sourceType !== 'technical-service') {
      return;
    }

    const requestId = Number(selectedDraft.sourceId);
    if (!requestId) {
      return;
    }

    const currentRequest = technicalServiceRequests.find((request) => request.id === requestId);
    const hasLoadedDetails =
      Array.isArray(currentRequest?.usedParts) ||
      Array.isArray(currentRequest?.workLogs);

    if (hasLoadedDetails) {
      return;
    }

    let cancelled = false;

    const loadSelectedTechnicalDetail = async () => {
      try {
        const detailResponse = await api.get(`/TechnicalService/requests/${requestId}`);
        const detail = detailResponse?.data?.data;

        if (!detail || cancelled) {
          return;
        }

        setTechnicalServiceRequests((current) =>
          current.map((request) => (request.id === requestId ? detail : request))
        );
      } catch (detailError) {
        console.error('Seçili teknik servis fatura detayı alınamadı:', detailError);
      }
    };

    loadSelectedTechnicalDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedDraft, technicalServiceRequests]);

  const handleDraftItemChange = (itemId, field, value) => {
    setDraftItems((current) =>
      current.map((item) =>
        item.localId === itemId
          ? {
              ...item,
              [field]: field === 'vatRate' ? Number(value) : value
            }
          : item
      )
    );
  };

  const draftTotals = useMemo(() => {
    return draftItems.reduce(
      (acc, item) => {
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unitPrice || 0);
        const vatRate = Number(item.vatRate || 0);
        const subTotal = quantity * unitPrice;
        const vatAmount = subTotal * (vatRate / 100);

        acc.subTotal += subTotal;
        acc.totalVat += vatAmount;
        acc.total += subTotal + vatAmount;
        return acc;
      },
      {
        subTotal: 0,
        totalVat: 0,
        total: 0
      }
    );
  }, [draftItems]);

  const draftValidation = useMemo(() => {
    const missingFields = [];

    if (!draftForm.companyName.trim()) missingFields.push('Firma Ünvanı');
    if (!draftForm.taxNo.trim()) missingFields.push('Vergi Numarası');
    if (!draftForm.address.trim()) missingFields.push('Adres');

    return {
      ready: missingFields.length === 0,
      missingFields
    };
  }, [draftForm.address, draftForm.companyName, draftForm.taxNo]);

  const totals = useMemo(
    () =>
      draftCandidates.reduce(
        (acc, draft) => {
          acc.totalAmount += Number(draft.amount || 0);
          acc.totalCount += 1;
          if (draft.type === 'rental') acc.rentalAmount += Number(draft.amount || 0);
          if (draft.type === 'internal-service') {
            acc.internalServiceAmount += Number(draft.amount || 0);
          }
          if (draft.type === 'external-service') {
            acc.externalServiceAmount += Number(draft.amount || 0);
          }
          return acc;
        },
        {
          totalAmount: 0,
          totalCount: 0,
          rentalAmount: 0,
          internalServiceAmount: 0,
          externalServiceAmount: 0
        }
      ),
    [draftCandidates]
  );

  const handleCutInvoiceLegacy = () => {
    if (!selectedDraft) return;

    if (!draftValidation.ready) {
      setActionMessage('');
      setActionError(
        `Fatura kesmeden önce şu alanları doldurmalıyız: ${draftValidation.missingFields.join(
          ', '
        )}`
      );
      return;
    }

    setActionError('');
      setActionMessage(
        `${selectedDraft.companyName} için taslak kesime hazır. Faturanet bağlantısı geldiğinde bu buton doğrudan faturayı kesecek.`
      );
    setPreviewOpen(true);
  };

  const handleCutInvoice = async () => {
    if (!selectedDraft || isReadOnlyOwner || isSending) return;

    if (!draftValidation.ready) {
      setActionMessage('');
      setActionError(
        `Fatura kesmeden önce şu alanları doldurmalıyız: ${draftValidation.missingFields.join(
          ', '
        )}`
      );
      return;
    }

    const validItems = draftItems.filter(
      (item) =>
        pickText(item.description).trim() &&
        Number(item.quantity || 0) > 0 &&
        Number(item.unitPrice || 0) > 0
    );

    if (!validItems.length) {
      setActionMessage('');
      setActionError('Fatura kesebilmek için en az bir geçerli kalem gerekiyor.');
      return;
    }

    try {
      setIsSending(true);
      setActionError('');
      setActionMessage('');

      const lookupResponse = await api.post('/NetteFatura/customer-lookup', {
        taxNumber: draftForm.taxNo.trim()
      });

      const lookupData = lookupResponse?.data || {};
      const resolvedTaxNumber = pickText(lookupData?.taxNumber, draftForm.taxNo).trim();

      if (!lookupData?.isSuccess) {
        setActionMessage('');
        setActionError(
          lookupData?.message || 'Vergi numarası doğrulanamadığı için fatura gönderimi durduruldu.'
        );
        return;
      }

      const sendResponse = await api.post('/NetteFatura/send-manual-invoice', {
        taxNumber: resolvedTaxNumber,
        companyName: draftForm.companyName.trim(),
        address: draftForm.address.trim(),
        email: draftForm.email.trim(),
        phone: draftForm.phone.trim(),
        note: draftForm.note.trim(),
        invoiceProfile: lookupData?.invoiceProfile || 'e-Arşiv',
        receiverInboxTag: lookupData?.inboxTags?.[0] || '',
        receiverInboxTags: lookupData?.inboxTags || [],
        sourceType: selectedDraft.sourceType || '',
        sourceId: selectedDraft.sourceId || '',
        lines: validItems.map((item) => ({
          description: pickText(item.description),
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          vatRate: Number(item.vatRate || 0)
        }))
      });

      const sendData = sendResponse?.data || {};

      if (!sendData?.isSuccess) {
        setActionError(sendData?.message || 'Fatura gönderimi başarısız oldu.');
        return;
      }

      const successParts = [sendData?.message];

      if (sendData?.invoiceNumber) {
        successParts.push(`Belge No: ${sendData.invoiceNumber}`);
      }

      if (sendData?.ettn) {
        successParts.push(`ETTN: ${sendData.ettn}`);
      }

      const invoiceRecord = {
        draftId: selectedDraft.id,
        type: selectedDraft.type,
        sourceId: selectedDraft.id.replace(/^(technical-|rental-)/, ''),
        companyName: draftForm.companyName.trim(),
        rentalIds: selectedDraft.type === 'rental' ? selectedDraft.rentalIds || [] : [],
        rentalInstallments:
          selectedDraft.type === 'rental' ? selectedDraft.rentalInstallments || [] : [],
        rentalOneTimeItemIds:
          selectedDraft.type === 'rental' ? selectedDraft.rentalOneTimeItemIds || [] : [],
        invoiceNumber: sendData?.invoiceNumber || '',
        ettn: sendData?.ettn || '',
        sentAt: new Date().toISOString(),
        statusLabel: 'Tamamlandi',
        infoLabel: 'Fatura Kesildi'
      };

      const nextInvoicedDrafts = {
        ...invoicedDrafts,
        [selectedDraft.id]: invoiceRecord
      };

      setInvoicedDrafts(nextInvoicedDrafts);
      writeInvoicedDrafts(nextInvoicedDrafts);
      setSuccessNotice(`Fatura başarıyla kesildi. ${successParts.filter(Boolean).join(' | ')}`);
      setActionMessage('');
      setPreviewOpen(false);
    } catch (error) {
      setActionError(
        error?.response?.data?.message ||
          error?.response?.data ||
          'Fatura gönderimi sırasında bir hata oluştu.'
      );
    } finally {
      setIsSending(false);
    }
  };


  return (
    <div className="invoice-drafts-page">
      <Navbar />
      <div className="invoice-drafts-container">
        <header className="invoice-drafts-header">
          <div>
            <h1>Fatura İşlemleri</h1>
          </div>
          {(userRole === 'Owner' || userRole === 'Accountant' || userRole === 'RentalAccountant') && (
            <Link to="/accounting/manual-invoice" className="invoice-drafts-manual-link">
              <FaPlus /> Manuel Fatura Oluştur
            </Link>
          )}
        </header>

        {loading && <div className="invoice-drafts-loading">Fatura İşlemleri Yükleniyor...</div>}
        {errorMessage && <div className="invoice-drafts-alert">{errorMessage}</div>}
        {successNotice && (
          <div className="invoice-drafts-toast success" role="status">
            <div className="invoice-drafts-toast__body">
              <strong>Fatura başarıyla oluşturuldu</strong>
              <span>{successNotice}</span>
            </div>
            <button
              type="button"
              className="invoice-drafts-toast__close"
              onClick={() => setSuccessNotice('')}
              aria-label="Başarı mesajını kapat"
            >
              ×
            </button>
          </div>
        )}

        <section className="invoice-drafts-summary">
          <article className="invoice-drafts-summary__card">
            <span>Toplam Taslak</span>
            <strong>{totals.totalCount}</strong>
          </article>
          <article className="invoice-drafts-summary__card">
            <span>Kiralama</span>
            <strong>{formatCurrency(totals.rentalAmount)}</strong>
          </article>
          <article className="invoice-drafts-summary__card">
            <span>İç Teknik Servis</span>
            <strong>{formatCurrency(totals.internalServiceAmount)}</strong>
          </article>
          <article className="invoice-drafts-summary__card accent">
            <span>Dış Teknik Servis</span>
            <strong>{formatCurrency(totals.externalServiceAmount)}</strong>
          </article>
        </section>

        <section className="invoice-drafts-filters">
          <div className="invoice-drafts-filters__title">
            <FaFilter />
            <span>Filtreler</span>
          </div>

          <div className="invoice-drafts-filters__grid">
            <label>
              <span>Taslak Tipi</span>
              <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                <option value="all">Tümü</option>
                <option value="rental">Kiralama</option>
                <option value="internal-service">İç Teknik Servis</option>
                <option value="external-service">Dış Teknik Servis</option>
              </select>
            </label>

            <label>
              <span>Firma</span>
              <select
                value={companyFilter}
                onChange={(event) => setCompanyFilter(event.target.value)}
              >
                <option value="all">Tüm Firmalar</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="invoice-drafts-filters__search">
              <span>Arama</span>
              <input
                type="text"
                placeholder="Firma, makine, talep no..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>
          </div>
        </section>

        <section className={`invoice-drafts-layout ${isReadOnlyOwner ? 'owner-view' : ''}`}>
          <div className="invoice-drafts-list">
            <div className="invoice-drafts-list__header">
              <h2>Bekleyen Fatura İşlemleri</h2>
              <span>
                {filteredDrafts.length} kayıt
                {filteredDrafts.length > DRAFTS_PAGE_SIZE ? ` | ${draftPage}/${draftPageCount}` : ''}
              </span>
            </div>

            {filteredDrafts.length > 0 ? (
              <>
                <div className="invoice-drafts-list__items">
                  {paginatedDrafts.map((draft) => {
                    const icon = draft.type === 'rental' ? <FaTruckMoving /> : <FaTools />;

                    return (
                      <button
                        key={draft.id}
                        type="button"
                        className={`invoice-draft-card ${
                          !isReadOnlyOwner && selectedDraft?.id === draft.id ? 'active' : ''
                        }`}
                        onClick={() => !isReadOnlyOwner && setSelectedDraftId(draft.id)}
                        disabled={isReadOnlyOwner}
                      >
                        <div className="invoice-draft-card__top">
                          <span className={`invoice-draft-card__badge type-${draft.type}`}>
                            {draft.typeLabel}
                          </span>
                          <span className="invoice-draft-card__amount">
                            {formatCurrency(draft.amount)}
                          </span>
                        </div>

                        <div className="invoice-draft-card__title">
                          <span className="invoice-draft-card__icon">{icon}</span>
                          <div>
                            <strong>{draft.companyName}</strong>
                            <p>{draft.title}</p>
                          </div>
                        </div>

                        <div className="invoice-draft-card__meta">
                          <span>Makine: {draft.machineLabel}</span>
                          <span>Kaynak: {draft.sourceLabel}</span>
                          <span>Tarih: {draft.dateLabel}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {filteredDrafts.length > DRAFTS_PAGE_SIZE && (
                  <div className="invoice-drafts-pagination">
                    <button
                      type="button"
                      onClick={() => setDraftPage((current) => Math.max(1, current - 1))}
                      disabled={draftPage === 1}
                      aria-label="Önceki sayfa"
                    >
                      <FaChevronLeft />
                    </button>
                    <span>
                      {draftPage} / {draftPageCount}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setDraftPage((current) => Math.min(draftPageCount, current + 1))
                      }
                      disabled={draftPage === draftPageCount}
                      aria-label="Sonraki sayfa"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            ) : loading ? (
              <div className="invoice-drafts-empty">
                <h3>Fatura İşlemleri Yükleniyor...</h3>
                <p>Liste hazırlanıyor, birkaç saniye içinde kayıtlar görünecek.</p>
              </div>
            ) : (
              <div className="invoice-drafts-empty">
                <h3>Hazır fatura kaydı bulunmuyor</h3>
                <p>Seçtiğin filtrelere göre faturaya dönüşebilecek aktif bir kayıt görünmüyor.</p>
              </div>
            )}
          </div>

          {!isReadOnlyOwner && (
            <div className="invoice-drafts-detail">
              {selectedDraft ? (
                <>
                  <div className="invoice-drafts-detail__header">
                    <div>
                      <h2>Taslak Önizleme</h2>
                      <p>
                        {selectedDraft.typeLabel} kaydı için fatura bilgilerini burada son kez
                        düzenleyebilirsin.
                      </p>
                    </div>
                    <div className="invoice-drafts-detail__actions">
                      <button
                        type="button"
                        className="preview-button preview-button--ghost"
                        onClick={() => setPreviewOpen(true)}
                      >
                        Önizlemeyi Aç <FaExternalLinkAlt />
                      </button>
                      <button
                        type="button"
                        className="preview-button"
                        onClick={handleCutInvoice}
                        disabled={INVOICE_SENDING_DISABLED || isSending}
                      >
                        {INVOICE_SENDING_DISABLED ? 'Fatura Kes Kapalı' : isSending ? 'Gönderiliyor...' : 'Fatura Kes'}
                      </button>
                    </div>
                  </div>

                  <div className="invoice-drafts-status-strip">
                    <div className="invoice-drafts-status-strip__item">
                      <span>Durum</span>
                      <strong>{draftValidation.ready ? 'Kesime Hazır' : 'Bilgi Bekleniyor'}</strong>
                    </div>
                    <div className="invoice-drafts-status-strip__item">
                      <span>Taslak Tipi</span>
                      <strong>{selectedDraft.typeLabel}</strong>
                    </div>
                    <div className="invoice-drafts-status-strip__item">
                      <span>Kaynak</span>
                      <strong>{selectedDraft.sourceLabel}</strong>
                    </div>
                  </div>

                  {actionError && (
                    <div className="invoice-drafts-inline-alert error">{actionError}</div>
                  )}
                  {actionMessage && (
                    <div className="invoice-drafts-inline-alert success">{actionMessage}</div>
                  )}

                  <div className="invoice-drafts-detail__form">
                    <label>
                      <span>Firma Ünvanı</span>
                      <input
                        type="text"
                        value={draftForm.companyName}
                        onChange={(event) =>
                          setDraftForm((current) => ({
                            ...current,
                            companyName: event.target.value
                          }))
                        }
                      />
                    </label>

                    <label>
                      <span>Vergi Numarası</span>
                      <input
                        type="text"
                        value={draftForm.taxNo}
                        onChange={(event) =>
                          setDraftForm((current) => ({ ...current, taxNo: event.target.value }))
                        }
                      />
                    </label>

                    <label>
                      <span>Telefon</span>
                      <input
                        type="text"
                        value={draftForm.phone}
                        onChange={(event) =>
                          setDraftForm((current) => ({ ...current, phone: event.target.value }))
                        }
                      />
                    </label>

                    <label className="full">
                      <span>Adres</span>
                      <textarea
                        rows="3"
                        value={draftForm.address}
                        onChange={(event) =>
                          setDraftForm((current) => ({
                            ...current,
                            address: event.target.value
                          }))
                        }
                      />
                    </label>

                    <label className="full">
                      <span>Not</span>
                      <textarea
                        rows="3"
                        placeholder="İstersen faturaya düşecek kısa notu burada hazırlayabilirsin."
                        value={draftForm.note}
                        onChange={(event) =>
                          setDraftForm((current) => ({ ...current, note: event.target.value }))
                        }
                      />
                    </label>
                  </div>

                  <div className="invoice-drafts-detail__table">
                    <div className="invoice-drafts-detail__table-header">
                      <h3>Kalemler</h3>
                      <span>{draftItems.length} kalem</span>
                    </div>

                    <div className="invoice-drafts-lines">
                      {draftItems.map((item) => {
                        const quantity = Number(item.quantity || 0);
                        const unitPrice = Number(item.unitPrice || 0);
                        const vatRate = Number(item.vatRate || 0);
                        const lineSubTotal = quantity * unitPrice;
                        const lineVat = lineSubTotal * (vatRate / 100);
                        const lineTotal = lineSubTotal + lineVat;

                        return (
                        <div key={item.localId} className="invoice-drafts-line">
                          <div className="invoice-drafts-line__description">
                            <strong>{item.description}</strong>
                            <span>Miktar: {item.quantity}</span>
                          </div>
                          <div className="invoice-drafts-line__vat">
                            <span>KDV Oranı</span>
                            <select
                              value={vatRate}
                              onChange={(event) =>
                                handleDraftItemChange(item.localId, 'vatRate', event.target.value)
                              }
                            >
                              {VAT_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  %{option}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="invoice-drafts-line__numbers">
                            <span>Birim: {formatCurrency(unitPrice)}</span>
                            <span>KDV: {formatCurrency(lineVat)}</span>
                            <strong>{formatCurrency(lineTotal)}</strong>
                          </div>
                        </div>
                      )})}
                    </div>
                  </div>

                  <div className="invoice-drafts-detail__footer">
                    <div>
                      <span>Ara Toplam</span>
                      <strong>{formatCurrency(draftTotals.subTotal)}</strong>
                    </div>
                    <div>
                      <span>Toplam KDV</span>
                      <strong>{formatCurrency(draftTotals.totalVat)}</strong>
                    </div>
                    <div>
                      <span>Kaynak</span>
                      <strong>{selectedDraft.sourceLabel}</strong>
                    </div>
                    <div>
                      <span>Genel Toplam</span>
                      <strong>{formatCurrency(draftTotals.total)}</strong>
                    </div>
                  </div>
                </>
              ) : (
                <div className="invoice-drafts-empty">
                  <h3>Taslak seçimi bekleniyor</h3>
                  <p>
                    Soldaki taslak adaylarından birini seçtiğinde sağ tarafta fatura önizlemesi
                    açılacak.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {!isReadOnlyOwner && previewOpen && selectedDraft && (
        <div
          className="invoice-preview-modal"
          role="presentation"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="invoice-preview-modal__content"
            role="dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="invoice-preview-modal__header">
              <div>
                <h2>Fatura Taslağı Önizleme</h2>
                <p>{selectedDraft.typeLabel} için Faturanet öncesi son kontrol ekranı.</p>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => setPreviewOpen(false)}
              >
                <FaArrowLeft /> Geri
              </button>
            </div>

            <div className="invoice-preview-modal__meta">
              <div>
                <span>Firma</span>
                <strong>{draftForm.companyName || '-'}</strong>
              </div>
              <div>
                <span>Vergi No</span>
                <strong>{draftForm.taxNo || '-'}</strong>
              </div>
              <div>
                <span>Telefon</span>
                <strong>{draftForm.phone || '-'}</strong>
              </div>
              <div>
                <span>Tarih</span>
                <strong>{selectedDraft.dateLabel}</strong>
              </div>
            </div>

            <div className="invoice-preview-modal__address">
              <span>Adres</span>
              <strong>{draftForm.address || '-'}</strong>
            </div>

            <div className="invoice-preview-modal__lines">
              {draftItems.map((item) => {
                const quantity = Number(item.quantity || 0);
                const unitPrice = Number(item.unitPrice || 0);
                const vatRate = Number(item.vatRate || 0);
                const lineSubTotal = quantity * unitPrice;
                const lineVat = lineSubTotal * (vatRate / 100);
                const lineTotal = lineSubTotal + lineVat;

                return (
                <div key={item.localId} className="invoice-preview-modal__line">
                  <div>
                    <strong>{item.description}</strong>
                    <span>
                      Miktar: {item.quantity} | KDV: %{vatRate} | KDV Tutarı: {formatCurrency(lineVat)}
                    </span>
                  </div>
                  <strong>{formatCurrency(lineTotal)}</strong>
                </div>
              )})}
            </div>

            {draftForm.note && (
              <div className="invoice-preview-modal__note">
                <span>Not</span>
                <p>{draftForm.note}</p>
              </div>
            )}

            <div className="invoice-preview-modal__total">
              <span>
                Ara Toplam: {formatCurrency(draftTotals.subTotal)} | Toplam KDV: {formatCurrency(draftTotals.totalVat)}
              </span>
              <strong>{formatCurrency(draftTotals.total)}</strong>
            </div>

            <div className="invoice-preview-modal__footer">
              <button
                type="button"
                className="close-button close-button--ghost"
                onClick={() => setPreviewOpen(false)}
              >
                Düzenlemeye Dön
              </button>
              <button type="button" className="close-button" onClick={handleCutInvoice} disabled={INVOICE_SENDING_DISABLED || isSending}>
                {INVOICE_SENDING_DISABLED ? 'Fatura Kes Kapalı' : isSending ? 'Gönderiliyor...' : 'Fatura Kes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDraftsPage;







